import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Stamp } from "@/components/Doc";

const pad = (n) => String(n).padStart(2, "0");
const letters = ["a", "b", "c", "d"];
const Rule = () => <div className="border-t border-carbon my-6" />;

const Head = ({ children }) => <h3 className="t-label mt-10 mb-4 rule-label"><span>{children}</span></h3>;

export default function TeamSheet() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get(`/team/${code}/sheet`, { params: { key: params.get("key") || "" } })
    .then((r) => setSheet(r.data)).catch((e) => setError(formatError(e)));

  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return (
    <main className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="team-sheet-error">
      <Stamp color="red" rotate={-6} size="lg">Restricted</Stamp>
      <p className="t-body text-lg mt-8">{error}</p>
      <p className="t-body text-base text-olive mt-2">This sheet opens only with the sponsor's private link.</p>
    </main>
  );
  if (!sheet) return <main className="min-h-[60vh] flex items-center justify-center t-label text-olive">Pulling the file…</main>;

  const { summary: s, respondents, questions, domains, widest } = sheet;

  return (
    <main className="px-5 sm:px-8 py-14 sm:py-20" data-testid="team-sheet-page">
      <div className="no-print max-w-4xl mx-auto flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
        <span className="t-label text-olive">Sponsor sheet · refreshes on reload</span>
        <div className="flex gap-3">
          <button onClick={() => { navigator.clipboard.writeText(sheet.invite_url); toast("Invite link copied"); }} className="btn" data-testid="sheet-copy-invite-btn">Copy Invite Link</button>
          <button onClick={() => window.print()} className="btn" data-testid="sheet-print-btn">Download as PDF</button>
        </div>
      </div>

      <article className="report-sheet bg-paper border border-carbon p-6 sm:p-12 max-w-4xl mx-auto shadow-[6px_6px_0_0_#D9C9A8]">
        <header className="flex justify-between items-start">
          <div>
            <div className="t-head text-3xl tracking-[0.08em]">LONGHAND</div>
            <div className="t-label text-olive mt-1">Team spread — Key Ring Audit</div>
          </div>
          <div className="text-right">
            <div className="t-label">Form LH-91</div>
            <div className="t-label text-olive mt-1">Code {sheet.code}</div>
          </div>
        </header>
        <Rule />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid grid-cols-[130px_1fr] gap-3">
            <span className="t-label text-olive py-1">Company:</span><span className="t-body text-lg" data-testid="sheet-company">{sheet.company}</span>
            <span className="t-label text-olive py-1">Convened by:</span><span className="t-body text-lg">{sheet.sponsor_name}</span>
            <span className="t-label text-olive py-1">Respondents:</span><span className="t-body text-lg" data-testid="sheet-count">{s.count}</span>
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3 items-center">
            <span className="t-label text-olive py-1">Team mean:</span><span className="t-body text-lg" data-testid="sheet-mean">{s.count ? `${s.mean_total} of 80` : "—"}</span>
            <span className="t-label text-olive py-1">Range:</span><span className="t-body text-lg">{s.count ? `${s.min_total} – ${s.max_total}` : "—"}</span>
            <span className="t-label text-olive py-1">Reading:</span>
            <span>{s.count ? <Stamp color="red" rotate={-4} size="md" testId="sheet-band">{s.band}</Stamp> : <span className="t-label text-rule">Awaiting responses</span>}</span>
          </div>
        </div>
        <Rule />

        {s.count === 0 ? (
          <div className="py-10 text-center" data-testid="sheet-empty">
            <p className="t-body text-lg">No responses filed yet.</p>
            <p className="t-body text-base text-olive mt-2">Send the invite link to the room. The sheet fills in as each person submits.</p>
            <p className="t-mono text-sm mt-6 break-all">{sheet.invite_url}</p>
          </div>
        ) : (
          <>
            {widest.length > 0 && (
              <>
                <Head>Where the room disagrees</Head>
                <p className="t-body text-base text-olive mb-4">The questions with the widest spread between answers. These are the agenda for the two days.</p>
                <div className="flex flex-col gap-3">
                  {widest.map((w, i) => (
                    <div key={w.id} className="grid grid-cols-[100px_1fr_auto] gap-3 items-start" data-testid={`widest-${i + 1}`}>
                      <span className="t-label pt-1">Q {pad(w.id)}</span>
                      <p className="t-body text-lg leading-snug">{w.text}</p>
                      <span className="t-label text-stamp pt-1 whitespace-nowrap">Spread {w.spread}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <Head>Exposure by domain — team mean</Head>
            <div className="flex flex-col gap-5">
              {domains.map((d) => (
                <div key={d.module} data-testid={`sheet-domain-${d.module}`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between t-label mb-1.5 gap-0.5">
                    <span>{d.numeral} — {d.name}</span>
                    <span className="text-olive">mean {d.mean} / {d.max} · range {d.min}–{d.max_seen}</span>
                  </div>
                  <div className="exposure-track relative">
                    <div className="exposure-fill" style={{ width: `${Math.round((d.mean / d.max) * 100)}%` }} />
                    <span className="absolute top-[-3px] h-[14px] w-[2px] bg-stamp" style={{ left: `${Math.round((d.max_seen / d.max) * 100)}%` }} title="Highest respondent" />
                  </div>
                </div>
              ))}
            </div>

            <Head>Respondents</Head>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="respondents-table">
                <thead>
                  <tr className="t-label text-olive border-b border-rule">
                    <th className="py-2 pr-3 font-normal">Name</th>
                    <th className="py-2 pr-3 font-normal">Role</th>
                    <th className="py-2 pr-3 font-normal">Total</th>
                    <th className="py-2 pr-3 font-normal">Band</th>
                    <th className="py-2 font-normal whitespace-nowrap">Answers 01–10</th>
                  </tr>
                </thead>
                <tbody>
                  {respondents.map((r) => (
                    <tr key={r.id} className="border-b border-rule/50 t-body text-base" data-testid={`respondent-${r.id}`}>
                      <td className="py-2 pr-3">{r.name}</td>
                      <td className="py-2 pr-3 text-olive">{r.role}</td>
                      <td className="py-2 pr-3 t-mono font-bold">{r.total}</td>
                      <td className="py-2 pr-3 t-label">{r.band}</td>
                      <td className="py-2 t-mono text-sm tracking-[0.3em]">{r.answers.map((a) => letters[a]).join("")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Head>Answer distribution by question</Head>
            <div className="flex flex-col gap-8">
              {questions.map((q) => (
                <div key={q.id} className="grid sm:grid-cols-[90px_1fr] gap-3" data-testid={`sheet-question-${q.id}`}>
                  <div>
                    <span className="t-label block">Q {pad(q.id)}</span>
                    <span className="t-label text-rule block mt-1">Spread {q.spread}</span>
                  </div>
                  <div>
                    <p className="t-body text-lg leading-snug">{q.text}</p>
                    <div className="mt-3 flex flex-col gap-1.5">
                      {q.answers.map((a, i) => (
                        <div key={i} className="grid grid-cols-[28px_1fr_120px_36px] gap-2 items-center">
                          <span className="t-label text-olive">({letters[i]})</span>
                          <span className="t-body text-base leading-tight">{a.label}</span>
                          <div className="exposure-track"><div className="exposure-fill" style={{ width: `${s.count ? Math.round((a.count / s.count) * 100) : 0}%`, background: i >= 2 ? "#B3312C" : "#1A1A18" }} /></div>
                          <span className="t-mono text-sm text-right">{a.count}</span>
                        </div>
                      ))}
                    </div>
                    <p className="t-label text-rule mt-2">{q.who.filter((w) => w.answer_index >= 2).map((w) => w.name).join(", ") || "Nobody"} answered (c) or (d)</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <Rule />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <span className="t-label text-olive max-w-xs">Originated by Longhand — Operating practice from before the machine</span>
          <Link to="/contact?interest=scoping-call" className="btn btn-stamp no-print" data-testid="sheet-book-call-btn">Book a Scoping Call</Link>
        </div>
      </article>
    </main>
  );
}
